import { AlertTriangle, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type MatrixCell,
  type MatrixMonth,
  useResourceGuidanceMatrix,
} from "@/hooks/use-resource-guidance-matrix";

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "2026-08" → "Aug" / "Aug '27" (year suffix only when it isn't the window's own year). */
function monthLabel(monthKey: string, showYear: boolean): string {
  const [yearStr, monthStr] = monthKey.split("-");
  const abbr = MONTH_ABBR[Number(monthStr) - 1] ?? monthKey;
  return showYear ? `${abbr} '${(yearStr ?? "").slice(2)}` : abbr;
}

/** Hours for display: whole numbers bare, fractions to one decimal, null as an em-dash. */
function fmtHours(hours: number | null): string {
  if (hours === null) return "–";
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

/**
 * One matrix cell. The current month is the only column that can carry BOTH an
 * actual and a guided value, and they are shown stacked rather than summed — the
 * whole point of the split is that "logged so far" and "planned" are different
 * facts. Elsewhere a column shows whichever half its `kind` implies.
 */
function Cell({ cell, month }: { cell: MatrixCell; month: MatrixMonth }) {
  const showActual = month.kind === "actual" || month.isCurrent;
  // A lagging NOS current-period can land guidance on a past column; show it
  // rather than silently dropping the hours (see the API module's WINDOW ANCHOR).
  const showGuided = month.kind === "guidance" || cell.guidedHours !== null;

  const empty =
    (!showActual || cell.actualHours === null) && (!showGuided || cell.guidedHours === null);
  if (empty) {
    return <td className="px-2 py-1.5 text-center text-[11px] text-muted-foreground/40">–</td>;
  }

  return (
    <td
      className={`px-2 py-1.5 text-center text-[11px] tabular-nums ${
        month.isCurrent ? "bg-brand-50 dark:bg-brand-950/40" : ""
      }`}
    >
      <div className="flex flex-col items-center gap-0.5">
        {showActual && cell.actualHours !== null ? (
          <span className="font-semibold text-foreground" title="Logged hours (actual)">
            {fmtHours(cell.actualHours)}h
          </span>
        ) : null}
        {showGuided && cell.guidedHours !== null ? (
          <span
            className={`${
              showActual && cell.actualHours !== null ? "text-[10px]" : "font-semibold"
            } text-brand-700 dark:text-brand-300`}
            title="Guided hours (planned in NOS)"
          >
            {fmtHours(cell.guidedHours)}h
          </span>
        ) : null}
      </div>
    </td>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceId: string;
  resourceName: string;
}

/**
 * The Guidance Outlook drill-down: Solutions (rows) × months (columns) for one
 * Resource, 3 months back → current → 3 months ahead. Past columns are logged
 * actuals from local hours_entry; the current and forward columns are NOS guidance
 * split per Solution; the current column carries both.
 *
 * Read-only. Fetches only while open (`enabled`), because the underlying read asks
 * NOS about every linked demand.
 */
export function GuidanceMatrixDialog({ open, onOpenChange, resourceId, resourceName }: Props) {
  const { data, isLoading, isError, error, refetch } = useResourceGuidanceMatrix(resourceId, open);

  const months = data?.months ?? [];
  // Only disambiguate years when the window actually straddles one.
  const spansYears = new Set(months.map((m) => m.month.slice(0, 4))).size > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(96vw,1100px)]">
        <DialogHeader>
          <DialogTitle>Guidance by Solution — {resourceName}</DialogTitle>
          <DialogDescription>
            Logged hours for past months, NOS guidance for the current month onward. The current
            month shows both: logged so far, then planned.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="p-6 text-center text-[12px] text-muted-foreground">Loading guidance…</div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-3 p-6 text-center">
            <AlertTriangle className="size-5 text-destructive" />
            <div className="text-[12px] text-muted-foreground">
              {error?.message ?? "Could not load the guidance matrix."}
            </div>
            <button
              type="button"
              onClick={refetch}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[11px] font-medium hover:bg-muted"
            >
              <RefreshCw className="size-3" />
              Retry
            </button>
          </div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-6 text-center text-[12px] italic text-muted-foreground">
            {data?.personUnmapped
              ? "This Resource isn’t linked to a NOS Person, so no guidance can be resolved."
              : "No guidance or logged hours for this Resource in this window."}
          </div>
        ) : (
          <>
            {data.guidanceUnavailable ? (
              <div className="mb-2 flex items-center gap-2 rounded-md border border-warning-200 bg-warning-50 px-2.5 py-1.5 text-[11px] text-warning-950 dark:border-warning-900 dark:bg-warning-950/30 dark:text-warning-100">
                <AlertTriangle className="size-3.5 shrink-0" />
                NOS guidance is unavailable right now — forward months are blank. Logged hours are
                unaffected.
              </div>
            ) : null}
            {data.nosCurrentMonth ? (
              <div className="mb-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                NOS’s current period is {monthLabel(data.nosCurrentMonth, true)}, which differs from
                the calendar month. Its guidance is shown in the month it belongs to.
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[11px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="sticky left-0 z-10 bg-background px-2 py-1.5 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Solution
                    </th>
                    {months.map((m) => (
                      <th
                        key={m.month}
                        className={`px-2 py-1.5 text-center font-semibold uppercase tracking-wider ${
                          m.isCurrent
                            ? "bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
                            : "text-muted-foreground"
                        }`}
                      >
                        {monthLabel(m.month, spansYears)}
                        <div className="text-[9px] font-medium normal-case tracking-normal opacity-70">
                          {m.isCurrent
                            ? "actual · guided"
                            : m.kind === "actual"
                              ? "actual"
                              : "guided"}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.solutionId} className="border-b border-border/50">
                      <td className="sticky left-0 z-10 max-w-[220px] truncate bg-background px-2 py-1.5 font-medium">
                        {row.solutionName}
                      </td>
                      {row.cells.map((cell, i) => {
                        const m = months[i];
                        return m ? <Cell key={cell.month} cell={cell} month={m} /> : null;
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border font-semibold">
                    <td className="sticky left-0 z-10 bg-background px-2 py-1.5 uppercase tracking-wider text-muted-foreground">
                      Total
                    </td>
                    {data.totals.map((cell, i) => {
                      const m = months[i];
                      return m ? <Cell key={cell.month} cell={cell} month={m} /> : null;
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <span className="font-semibold text-foreground">00h</span> logged (actual)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="font-semibold text-brand-700 dark:text-brand-300">00h</span> guided
                (NOS)
              </span>
              <span>·</span>
              <span>“–” means no data, not zero</span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
